import React, { useState } from 'react'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core'
import QrReader from 'react-qr-reader'
import { green, red } from '@material-ui/core/colors'

import { COLORS, FONTS } from 'styles'
import { useApiCall } from 'hooks'
import { DateUtils } from 'utils'
import { snackbar } from 'components'

import { InvoiceApi } from './api'
import { ExistsInvoiceParams, ExistsInvoiceResponse, InvoicePayload } from './types'

const TICKET_URL = 'efactura.dgi.gub.uy'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '100vh',
    padding: theme.spacing(2),
  },
  scanner: {
    width: '100%',
    maxWidth: '400px',
    marginTop: theme.spacing(4),
  },
  button: {
    fontFamily: FONTS.GOTHAM_LIGHT,
    fontSize: '18px',
    backgroundColor: theme.palette.primary.main,
    textTransform: 'capitalize',
    height: '45px',
    marginTop: 40,
    color: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  alertError: {
    backgroundColor: red[100],
    padding: 20,
    marginTop: 20,
    borderRadius: 8,
  },
  alertErrorText: {
    color: red[700],
  },
  alertSuccess: {
    backgroundColor: green[100],
    padding: 20,
    marginTop: 20,
    borderRadius: 8,
  },
  alertSuccessText: {
    color: green[700],
  },
  ticketContainer: {
    display: 'flex',
    borderWidth: 1,
    borderColor: COLORS.GREY_DARK,
    gap: 15,
    borderStyle: 'solid',
    width: 300,
    paddingRight: 20,
    paddingLeft: 20,
    paddingBottom: 15,
    paddingTop: 5,
    marginTop: 30,
  },
  ticketInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 1,
    borderBottomColor: COLORS.GREY_INPUT,
    borderBottomStyle: 'solid',
    paddingBottom: 15,
    paddingTop: 15,
  },
  date: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 15,
  },
  title: {
    fontFamily: FONTS.GOTHAM_LIGHT,
    fontSize: 30,
    fontWeight: 'bold',
  },
}))

interface Invoice {
  storeTaxId: string
  series: string
  number: string
  amount: number
  date: string
}

export const CheckInvoice: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()

  const [invoice, setInvoice] = useState<Invoice>()
  const [existsInvoice, setExistsInvoice] = useState<boolean>()
  const [user, setUser] = useState<{ email: string; fullname: string }>()
  const [enabledScan, setEnabledScan] = useState(true)

  const [existsInvoiceApi] = useApiCall<ExistsInvoiceParams, ExistsInvoiceResponse>(
    InvoiceApi.exists,
  )

  const [createInvoiceApi, isLoadingCreateInvoice] = useApiCall<InvoicePayload, any>(
    InvoiceApi.create,
  )

  const handleScan = async (data: string | null) => {
    if (enabledScan && data?.includes(TICKET_URL)) {
      setEnabledScan(false)
      try {
        const values = data.split('cfe?')[1].split(',')

        const diferenceDays = moment().diff(
          DateUtils.formatString(decodeURIComponent(values[5]), DateUtils.POSSIBLE_DATE_FORMATS),
          'days',
        )

        if (diferenceDays >= 3) {
          snackbar.show('No se pudo escanear el ticket porque ya pasaron 72 horas desde su emisión')
          return
        }

        const invoiceFromQr = {
          storeTaxId: values[0],
          series: values[2],
          number: values[3],
          amount: Number(values[4]),
          date: values[5],
        }
        const { exists, user: userInvoice } = await existsInvoiceApi({
          storeTaxId: invoiceFromQr.storeTaxId,
          number: invoiceFromQr.number,
        })
        setUser(userInvoice)
        setExistsInvoice(exists)
        setInvoice(invoiceFromQr)
      } catch (error) {
        snackbar.show('Ocurrió un error')
      } finally {
        setTimeout(() => {
          setEnabledScan(true)
        }, 2000)
      }
    }
  }

  const createInvoice = async () => {
    if (invoice) {
      try {
        let adminId
        const adminStorage = localStorage.getItem('admin')

        if (adminStorage) {
          const admin = JSON.parse(adminStorage) as { id: number }
          adminId = admin.id
        }

        await createInvoiceApi({
          serie: invoice.series,
          serieNumber: invoice.number,
          amount: invoice.amount * 100,
          currency: 'UYU',
          date: DateUtils.formatString(
            decodeURIComponent(invoice.date),
            DateUtils.POSSIBLE_DATE_FORMATS,
          ),
          rut: invoice.storeTaxId,
          photo: null,
          adminId,
        })
        snackbar.show('La factura ha sido guardada')
        history.push('/invoice/menu')
      } catch (error) {
        snackbar.show('Ocurrió un error')
      }
    }
  }

  const handleError = (err: Error) => {
    console.error(err)
  }

  return (
    <Container className={classes.container}>
      <Typography className={classes.title}>Chequear factura</Typography>

      {invoice ? (
        <>
          {existsInvoice ? (
            <div className={classes.alertError}>
              <Typography className={classes.alertErrorText}>
                Esta factura ya ha sido escaneada exitosamente por un usuario
              </Typography>
              {!!user?.email && (
                <div>
                  <Typography className={classes.alertErrorText} align="center">
                    {user.fullname}
                  </Typography>
                  <Typography className={classes.alertErrorText} align="center">
                    {user.email}
                  </Typography>
                </div>
              )}
            </div>
          ) : (
            <div className={classes.alertSuccess}>
              <Typography className={classes.alertSuccessText}>
                Esta factura aún no ha sido escaneada
              </Typography>
            </div>
          )}

          <Grid container direction="column" className={classes.ticketContainer}>
            <div className={classes.ticketInfo}>
              <Typography>Número de factura</Typography>
              <Typography>{invoice.number}</Typography>
            </div>
            <div className={classes.ticketInfo}>
              <Typography>Marca</Typography>
              <Typography>{invoice.storeTaxId}</Typography>
            </div>
            <div className={classes.ticketInfo}>
              <Typography>Monto</Typography>
              <Typography>{invoice.amount}</Typography>
            </div>
            <div className={classes.date}>
              <Typography>Fecha</Typography>
              <Typography>{invoice.date}</Typography>
            </div>
          </Grid>

          {!existsInvoice && (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={createInvoice}
              disabled={isLoadingCreateInvoice}
            >
              Quemar factura
            </Button>
          )}
        </>
      ) : (
        <QrReader
          delay={500}
          onError={handleError}
          onScan={handleScan}
          style={{ width: '100%' }}
          className={classes.scanner}
        />
      )}

      <Button
        variant="outlined"
        color="primary"
        style={{ marginTop: 40 }}
        onClick={() => history.push('/invoice/menu')}
        disabled={isLoadingCreateInvoice}
      >
        Volver
      </Button>
    </Container>
  )
}
